import { FC } from 'react';
import Confirmation from '../../UI/Pages/Confirmation/Confirmation';
import EnterPhoneNumber from '../../UI/Pages/EnterPhoneNumber/EnterPhoneNumber';
import ValidateOtp from '../../UI/Pages/ValidatePhoneNumber/ValidatePhoneNumber';
import Onboarding from '../../UI/Pages/Onboarding/Onboarding';
import OthersFront from '../../UI/Pages/Others/OthersFront';
import OthersBack from '../../UI/Pages/Others/OthersBack';
import AdditionalDocument from '../../UI/Pages/AdditionalDocuments/AdditionalDocument';
import PersonalDocuments from '../../UI/Pages/PersonalDocuments/PersonalDocuments';
import ReviewOthersDetails from '../../UI/Pages/OthersDetails/ReviewOthersDetails';
import StandardReviewDetails from '../../UI/Pages/StandardReviewDetails/StandardReviewDetails';
import StandardInstructionDetails from '../../UI/Pages/StandardInstruction/StandardInstructionDetails';
import TermsAndConditions from '../../UI/Pages/TermsAndConditions/TermsAndConditions';
import Selfie from '../../UI/Pages/Selfie/Selfie';
import { IFormStepProps } from '../../UI/Pages/PageWrapper/PageWrapper';
import CookiePolicy from '../../UI/Pages/CookiePolicy/CookiePolicy';
import PrivacyPolicy from '../../UI/Pages/PrivacyPolicy/PrivacyPolicy';
import StarRating from '../../UI/Pages/Rating/Rating';
import ValidateAgent from '../../UI/Pages/AgentLogin/ValidateAgent/ValidateAgent';
import ValidateAgency from '../../UI/Pages/AgentLogin/ValidateAgency/ValidateAgency';
import CustomerValidation from '../../UI/Pages/Selfie/CustomerValidation/CustomerValidation';
import ValidateAgentPin from '../../UI/Pages/ValidateAgentPin/ValidateAgentPin';
import { VALIDATION_STEPS } from '../../UI/Pages/Selfie/ValidateFace/validate-face.types';
import { BYPASS_LIVENESS_CHECK } from '../Constants';
import ValidateReverseOtp from '../../UI/Pages/ReverseOTP/Customer/ValidateReverseOtp';
import ValidateCustomer from '../../UI/Pages/ReverseOTP/Agent/ValidateCustomer/ValidateCustomer';
import ConfirmationMethod from '../../UI/Pages/ConfirmationMethod/ConfirmationMethod';
import UssdCustomerValidation from '../../UI/Pages/UssdCustomerValidation/UssdCustomerValidation';
import StandardInstructionDetailsBack from '../../UI/Pages/StandardInstruction/StandardInstructionDetailsBack';
import CustomerRegistrationManager from '../../Business/RegistrationManager/CustomerRegistrationManager';
import AgentRegistrationManager from '../../Business/RegistrationManager/AgentRegistrationManager';
import DI from '../DI';
import ValidateOtpExisting from '../../UI/Pages/ValidatePhoneNumber/ValidateOtpExisting';
import LiteUserMessage from '../../UI/Pages/ValidatePhoneNumber/LiteUserMessage';
import MsisdnUnderProgress from '../../UI/Pages/ValidatePhoneNumber/MsisdnUnderProgress';

export interface IFormStep {
    STEP: number;
    SCREEN: FC<IFormStepProps>;
    TITLE: string;
    KEY: keyof ISteps;
    PROPS?: any; // INFO: Use this property to past custom props to each/any form step screen
    PREVIOUS_STEP_HANDLER: ({ previousStep, isAgentLogged }: IPreviousStepHandler) => IFormStep;
    IS_NOT_VALIDATION_STEP?: boolean; // INFO: When true, indicates that the step isn't necessarily a validation step, eg: Cookie policy
    DONT_SHOW_ICON?: boolean; // INFO: When true, indicates a step where neither the home icon or the back button icon must be shown
    DONT_SHOW_LOGOUT_ICON?: boolean; // INFO: When true, indicates a step where the logout icon must not be shown
    SHOW_LANGUAGES_DROPDOWN?: boolean // When true, indicates a step where the dropdown to toggle the language is shown
}

export interface IPreviousStepHandler {
    previousStep?: IFormStep;
    isAgentLogged?: boolean;
    fromStepKey?: string;
}

export interface ISteps {
    ONBOARDING: string;
    ENTER_PHONE_NUMBER: string;
    VALIDATE_OTP: string;
    VALIDATE_OTP_EXISTING: string;
    LITE_USER_MESSAGE: string;
    PERSONAL_DOCUMENTS: string;
    REVIEW_OTHERS: string;
    METADATA_OTHERS:string;
    TERMS_AND_CONDITIONS: string;
    COOKIE_POLICY: string;
    PRIVACY_POLICY: string;
    OTHERS_FRONT: string;
    ADDITIONAL_DOCUMENT: string;
    OTHERS_BACK: string;
    STANDARD_INSTRUCTIONS_DETAILS: string;
    STANDARD_INSTRUCTIONS_DETAILS_BACK: string;
    STANDARD_REVIEW_DETAILS: string;
    SELFIE: string;
    CONFIRMATION: string;
    EVALUATE_EXPERIENCE: string;
    VALIDATE_AGENCY: string;
    VALIDATE_AGENT: string;
    CUSTOMER_VALIDATION: string;
    VALIDATE_AGENT_PIN: string;
    CUSTOMER_REVERSE_OTP: string;
    AGENT_REVERSE_OTP: String;
    CONFIRMATION_METHOD: String;
    USSD_PUSH_VALIDATION: string;
    MSISDN_REGISTERATION_UNDER_PROGRESS: string;
}

type IFormSteps = { [key in keyof ISteps]: IFormStep };

export const REVIEW_DETAILS_INFO = {
    TITLE: 'generalInformation',
    STEP: 5
};

const shouldBypassLivenessCheck = (isAgentLogged?: boolean) => (
    BYPASS_LIVENESS_CHECK
        ? FormSteps.PERSONAL_DOCUMENTS
        : (isAgentLogged ? FormSteps.CUSTOMER_VALIDATION : FormSteps.SELFIE)
);

export const redirectToPersonalDocuments = (isAgentLogged?: boolean): IFormStep => (
    isAgentLogged
        ? { ...FormSteps.PERSONAL_DOCUMENTS, TITLE: 'customerDocuments' }
        : FormSteps.PERSONAL_DOCUMENTS
);

const getIsWhitelistedUser = (): boolean => {
    try {
        const userManagerData = DI.get('UserManager');
        const result = userManagerData?.getWhiteListedMsisdn?.() ?? false;
        return result;
    } catch (e) {
        return false;
    }
};

const getStepBasedOnWhitelist = (defaultStep: number, whitelistedStep: number): number => {
    const isWhitelistedUser = getIsWhitelistedUser();
    return isWhitelistedUser ? whitelistedStep : defaultStep;
};

const FormSteps: IFormSteps = {
    ONBOARDING: {
        STEP: 0,
        SCREEN: Onboarding,
        TITLE: 'onboarding',
        KEY: 'ONBOARDING',
        PREVIOUS_STEP_HANDLER: () => FormSteps.ONBOARDING,
        DONT_SHOW_LOGOUT_ICON: true,
        IS_NOT_VALIDATION_STEP: true,
        SHOW_LANGUAGES_DROPDOWN: true
    },

    ENTER_PHONE_NUMBER: {
        STEP: 1,
        SCREEN: EnterPhoneNumber,
        TITLE: 'phoneNumber',
        KEY: 'ENTER_PHONE_NUMBER',
        PREVIOUS_STEP_HANDLER: () => FormSteps.ONBOARDING
    },

    VALIDATE_OTP: {
        STEP: 1,
        SCREEN: ValidateOtp,
        TITLE: 'phoneNumber',
        KEY: 'VALIDATE_OTP',
        PREVIOUS_STEP_HANDLER: () => FormSteps.ENTER_PHONE_NUMBER
    },

    VALIDATE_OTP_EXISTING: {
        STEP: 1,
        SCREEN: ValidateOtpExisting,
        TITLE: 'otpValidation',
        KEY: 'VALIDATE_OTP_EXISTING',
        PREVIOUS_STEP_HANDLER: () => FormSteps.VALIDATE_OTP_EXISTING
    },

    MSISDN_REGISTERATION_UNDER_PROGRESS: {
        STEP: 1,
        SCREEN: MsisdnUnderProgress,
        TITLE: 'MsisdnUnderProgress',
        KEY: 'MSISDN_REGISTERATION_UNDER_PROGRESS',
        PREVIOUS_STEP_HANDLER: () => FormSteps.MSISDN_REGISTERATION_UNDER_PROGRESS,
        DONT_SHOW_ICON: true
    },

    LITE_USER_MESSAGE: {
        STEP: 1,
        SCREEN: LiteUserMessage,
        TITLE: 'liteUserMessage',
        KEY: 'LITE_USER_MESSAGE',
        PREVIOUS_STEP_HANDLER: () => FormSteps.LITE_USER_MESSAGE
    },

    PERSONAL_DOCUMENTS: {
        STEP: 2,
        SCREEN: PersonalDocuments,
        TITLE: 'personalDocuments',
        KEY: 'PERSONAL_DOCUMENTS',
        PREVIOUS_STEP_HANDLER: () => FormSteps.VALIDATE_OTP,
        DONT_SHOW_ICON: true
    },

    STANDARD_REVIEW_DETAILS: {
        STEP: REVIEW_DETAILS_INFO.STEP,
        SCREEN: StandardReviewDetails,
        TITLE: REVIEW_DETAILS_INFO.TITLE,
        KEY: 'STANDARD_REVIEW_DETAILS',
        PREVIOUS_STEP_HANDLER: ({ isAgentLogged }) => shouldBypassLivenessCheck(isAgentLogged)
    },

    REVIEW_OTHERS: {
        STEP: 7,
        SCREEN: ReviewOthersDetails,
        TITLE: REVIEW_DETAILS_INFO.TITLE,
        KEY: 'REVIEW_OTHERS',
        PREVIOUS_STEP_HANDLER: ({ isAgentLogged }) => shouldBypassLivenessCheck(isAgentLogged)
    },

    METADATA_OTHERS: {
        STEP: 3,
        SCREEN: ReviewOthersDetails,
        TITLE: REVIEW_DETAILS_INFO.TITLE,
        KEY: 'METADATA_OTHERS',
        PREVIOUS_STEP_HANDLER: () => FormSteps.PERSONAL_DOCUMENTS
    },

    TERMS_AND_CONDITIONS: {
        STEP: 2,
        SCREEN: TermsAndConditions,
        TITLE: 'termsAndConditions',
        KEY: 'TERMS_AND_CONDITIONS',
        PREVIOUS_STEP_HANDLER: () => FormSteps.ONBOARDING,
        DONT_SHOW_ICON: true,
        SHOW_LANGUAGES_DROPDOWN: true,
        DONT_SHOW_LOGOUT_ICON: true
    },

    COOKIE_POLICY: {
        STEP: 0,
        SCREEN: CookiePolicy,
        TITLE: 'cookiePolicy',
        KEY: 'COOKIE_POLICY',
        PREVIOUS_STEP_HANDLER: () => FormSteps.ONBOARDING,
        IS_NOT_VALIDATION_STEP: true,
        DONT_SHOW_LOGOUT_ICON: true
    },

    PRIVACY_POLICY: {
        STEP: 0,
        SCREEN: PrivacyPolicy,
        TITLE: 'privacyPolicy',
        KEY: 'PRIVACY_POLICY',
        PREVIOUS_STEP_HANDLER: () => FormSteps.ONBOARDING,
        IS_NOT_VALIDATION_STEP: true,
        DONT_SHOW_LOGOUT_ICON: true
    },

    STANDARD_INSTRUCTIONS_DETAILS_BACK: {
        STEP: 3,
        SCREEN: StandardInstructionDetailsBack,
        TITLE: 'standardInstructionDetailsBack',
        KEY: 'STANDARD_INSTRUCTIONS_DETAILS_BACK',
        PREVIOUS_STEP_HANDLER: ({ isAgentLogged }) => redirectToPersonalDocuments(isAgentLogged)
    },

    OTHERS_FRONT: {
        STEP: 4,
        SCREEN: OthersFront,
        TITLE: 'othersFront',
        KEY: 'OTHERS_FRONT',
        PREVIOUS_STEP_HANDLER: () => FormSteps.METADATA_OTHERS
    },

    OTHERS_BACK: {
        STEP: 4,
        SCREEN: OthersBack,
        TITLE: 'othersBack',
        KEY: 'OTHERS_BACK',
        PREVIOUS_STEP_HANDLER: () => FormSteps.OTHERS_FRONT
    },

    STANDARD_INSTRUCTIONS_DETAILS: {
        STEP: 3,
        SCREEN: StandardInstructionDetails,
        TITLE: 'standardInstructionDetails',
        KEY: 'STANDARD_INSTRUCTIONS_DETAILS',
        PREVIOUS_STEP_HANDLER: ({ isAgentLogged }) => redirectToPersonalDocuments(isAgentLogged)
    },
    ADDITIONAL_DOCUMENT: {
        STEP: 0,
        SCREEN: AdditionalDocument,
        TITLE: 'additionalDocument',
        KEY: 'ADDITIONAL_DOCUMENT',
        // PREVIOUS_STEP_HANDLER: () => FormSteps.METADATA_OTHERS
        PREVIOUS_STEP_HANDLER: ({ fromStepKey }: IPreviousStepHandler = {}) => {
            if (fromStepKey === FormSteps.REVIEW_OTHERS.KEY) {
                return FormSteps.REVIEW_OTHERS;
            }
            return FormSteps.METADATA_OTHERS;
        }
    },
    SELFIE: {
        STEP: getStepBasedOnWhitelist(4, 5),
        SCREEN: Selfie,
        TITLE: 'validateYourIdentity',
        KEY: 'SELFIE',
        PREVIOUS_STEP_HANDLER: () => ((window as any)?.livelinessCheckCurrentStep === VALIDATION_STEPS.INIT
            ? { ...FormSteps.PERSONAL_DOCUMENTS, TITLE: 'personalDocuments' }
            : { ...FormSteps.SELFIE, PROPS: { initialStep: VALIDATION_STEPS.INIT } })
    },
    CUSTOMER_VALIDATION: {
        STEP: getStepBasedOnWhitelist(4, 5),
        SCREEN: CustomerValidation,
        TITLE: 'validateCustomerIdentity',
        KEY: 'CUSTOMER_VALIDATION',
        PREVIOUS_STEP_HANDLER: () => ((window as any)?.livelinessCheckCurrentStep === VALIDATION_STEPS.INIT
            ? { ...FormSteps.PERSONAL_DOCUMENTS, TITLE: 'customerDocuments' }
            : { ...FormSteps.CUSTOMER_VALIDATION, PROPS: { initialStep: VALIDATION_STEPS.INIT } })
    },
    // CONFIRMATION: {
    //     STEP: getStepBasedOnWhitelist(4, 5),
    //     SCREEN: Confirmation,
    //     TITLE: 'confirmation',
    //     KEY: 'CONFIRMATION',
    //     PREVIOUS_STEP_HANDLER: () => FormSteps.CONFIRMATION
    // },

    CONFIRMATION: {
        STEP: 0,
        SCREEN: Confirmation,
        TITLE: 'confirmation',
        KEY: 'CONFIRMATION',
        PREVIOUS_STEP_HANDLER: ({ isAgentLogged }: { isAgentLogged?: boolean }) => {
            const isWhitelistedUser = getIsWhitelistedUser();

            if (isWhitelistedUser) {
                return {
                    ...FormSteps.REVIEW_OTHERS,
                    STEP: 7
                };
            }

            if (isAgentLogged) {
                return {
                    ...FormSteps.SELFIE,
                    STEP: 5
                };
            }
            return {
                ...FormSteps.STANDARD_REVIEW_DETAILS,
                STEP: 5
            };
        },
        PROPS: ({ isAgentLogged }: { isAgentLogged?: boolean }) => {
            const isWhitelistedUser = getIsWhitelistedUser();
            let step;

            if (isWhitelistedUser) step = 8;
            else if (isAgentLogged) step = 5;
            else step = 7;
            return { STEP: step };
        }
    },
    EVALUATE_EXPERIENCE: {
        STEP: 0,
        SCREEN: StarRating,
        TITLE: 'rating',
        KEY: 'EVALUATE_EXPERIENCE',
        PREVIOUS_STEP_HANDLER: () => FormSteps.EVALUATE_EXPERIENCE,
        IS_NOT_VALIDATION_STEP: true,
        DONT_SHOW_ICON: true,
        DONT_SHOW_LOGOUT_ICON: true
    },
    VALIDATE_AGENCY: {
        STEP: 0,
        SCREEN: ValidateAgency,
        TITLE: 'shortCode',
        KEY: 'VALIDATE_AGENCY',
        PREVIOUS_STEP_HANDLER: () => FormSteps.ONBOARDING,
        IS_NOT_VALIDATION_STEP: true
    },
    VALIDATE_AGENT: {
        STEP: 0,
        SCREEN: ValidateAgent,
        TITLE: 'operatorID',
        KEY: 'VALIDATE_AGENT',
        PREVIOUS_STEP_HANDLER: () => FormSteps.VALIDATE_AGENCY,
        IS_NOT_VALIDATION_STEP: true
    },
    VALIDATE_AGENT_PIN: {
        STEP: 0,
        SCREEN: ValidateAgentPin,
        TITLE: 'validateAgentPin',
        KEY: 'VALIDATE_AGENT_PIN',
        PREVIOUS_STEP_HANDLER: () => FormSteps.VALIDATE_OTP,
        IS_NOT_VALIDATION_STEP: true,
        DONT_SHOW_ICON: true
    },
    CUSTOMER_REVERSE_OTP: {
        STEP: 4,
        SCREEN: ValidateReverseOtp,
        TITLE: 'confirmation',
        KEY: 'CUSTOMER_REVERSE_OTP',
        PREVIOUS_STEP_HANDLER: ({ previousStep }) => ({
            ...FormSteps.TERMS_AND_CONDITIONS,
            PROPS: {
                idParameter: previousStep?.PROPS?.idParameter
            }
        }),
        DONT_SHOW_LOGOUT_ICON: true
    },
    AGENT_REVERSE_OTP: {
        STEP: 6,
        SCREEN: ValidateCustomer,
        TITLE: 'confirmation',
        KEY: 'AGENT_REVERSE_OTP',
        PREVIOUS_STEP_HANDLER: ({ previousStep }) => ({
            ...FormSteps.CONFIRMATION_METHOD,
            PROPS: { REVIEW_DOCUMENT: previousStep!.PROPS.REVIEW_DOCUMENT }
        })
    },
    CONFIRMATION_METHOD: {
        STEP: 6,
        SCREEN: ConfirmationMethod,
        TITLE: 'confirmation',
        KEY: 'CONFIRMATION_METHOD',
        PREVIOUS_STEP_HANDLER: ({ previousStep }) => {
            const isMetaDataForm = previousStep?.PROPS?.isMetaDataForm;

            if (!isMetaDataForm) {
                return {
                    ...FormSteps.REVIEW_OTHERS,
                    PROPS: { visited: true }
                };
            }

            return {
                ...FormSteps[previousStep!.PROPS.REVIEW_DOCUMENT as keyof ISteps],
                PROPS: { visited: true }
            };
        }
    },
    USSD_PUSH_VALIDATION: {
        STEP: 6,
        SCREEN: UssdCustomerValidation,
        TITLE: 'confirmation',
        KEY: 'USSD_PUSH_VALIDATION',
        PREVIOUS_STEP_HANDLER: ({ previousStep }) => ({
            ...FormSteps.CONFIRMATION_METHOD,
            PROPS: { REVIEW_DOCUMENT: previousStep!.PROPS.REVIEW_DOCUMENT }
        })
    }
};

export default FormSteps;
